import { useEffect, useRef, useState, useCallback } from 'react';

interface UseInViewOptions {
  rootMargin?: string;
  threshold?: number | number[];
  root?: Element | null;
  triggerOnce?: boolean;
}

/**
 * Оптимизированный хук для отслеживания видимости элемента
 * с использованием Intersection Observer API
 */
export function useInView<T extends Element = Element>({
  rootMargin = '0px',
  threshold = 0,
  root = null,
  triggerOnce = false,
}: UseInViewOptions = {}): [
  (node: T | null) => void,
  boolean,
  IntersectionObserverEntry | undefined,
] {
  const [isInView, setIsInView] = useState(false);
  const [entry, setEntry] = useState<IntersectionObserverEntry>();
  const [hasTriggered, setHasTriggered] = useState(false);

  // Используем useRef для хранения observer и элемента
  const observerRef = useRef<IntersectionObserver | null>(null);
  const elementRef = useRef<T | null>(null);

  // Коллбэк для Intersection Observer
  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      const inView = entry.isIntersecting;

      setEntry(entry);

      // Если triggerOnce и уже сработал, не меняем состояние
      if (triggerOnce && hasTriggered && !inView) {
        return;
      }

      setIsInView(inView);

      if (inView && triggerOnce) {
        setHasTriggered(true);
      }
    },
    [triggerOnce, hasTriggered],
  );

  // Функция для установки ref элемента
  const setNodeRef = useCallback(
    (node: T | null) => {
      // Отключаем предыдущий observer
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      elementRef.current = node;

      if (node) {
        // Проверяем поддержку Intersection Observer
        if (typeof IntersectionObserver === 'undefined') {
          console.warn(
            'IntersectionObserver не поддерживается в этом браузере',
          );
          setIsInView(true); // Fallback - считаем что элемент всегда видим
          return;
        }

        // Создаем новый observer
        observerRef.current = new IntersectionObserver(handleIntersection, {
          rootMargin,
          threshold,
          root,
        });

        // Начинаем наблюдение
        observerRef.current.observe(node);
      }
    },
    [rootMargin, threshold, root, handleIntersection],
  );

  // Cleanup при размонтировании
  useEffect(
    () => () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    },
    [],
  );

  return [setNodeRef, isInView, entry];
}

// Дополнительный хук для более простых случаев - ИСПРАВЛЕНО
export function useInViewSimple<T extends Element = Element>(
  options: UseInViewOptions = {},
): [React.RefObject<T | null>, boolean] {
  const ref = useRef<T | null>(null);
  const [isInView, setIsInView] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      {
        rootMargin: options.rootMargin || '0px',
        threshold: options.threshold || 0,
        root: options.root || null,
      },
    );

    observer.observe(element);
    observerRef.current = observer;

    return () => {
      observer.disconnect();
    };
  }, [options.rootMargin, options.threshold, options.root]);

  return [ref, isInView];
}

// Хук для ленивой загрузки изображений
export function useLazyLoad<T extends Element = Element>(
  options: UseInViewOptions = {},
): [(node: T | null) => void, boolean, () => void] {
  const [shouldLoad, setShouldLoad] = useState(false);
  const [setRef, isInView] = useInView<T>({
    ...options,
    triggerOnce: true, // Загружаем только один раз
  });

  useEffect(() => {
    if (isInView && !shouldLoad) {
      setShouldLoad(true);
    }
  }, [isInView, shouldLoad]);

  const forceLoad = useCallback(() => {
    setShouldLoad(true);
  }, []);

  return [setRef, shouldLoad, forceLoad];
}

// Хук для видео автоплея
export function useVideoAutoplay<T extends HTMLVideoElement = HTMLVideoElement>(
  options: UseInViewOptions = {},
): [(node: T | null) => void, boolean, () => void] {
  const [shouldPlay, setShouldPlay] = useState(false);
  const videoRef = useRef<T | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const setVideoRef = useCallback(
    (node: T | null) => {
      // Отключаем предыдущий observer
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      videoRef.current = node;

      if (node) {
        if (typeof IntersectionObserver === 'undefined') {
          return;
        }

        // Создаем observer для видео элемента
        observerRef.current = new IntersectionObserver(
          ([entry]) => {
            const isInView = entry.isIntersecting;

            if (isInView && !shouldPlay) {
              const playPromise = node.play();
              if (playPromise) {
                playPromise.catch(() => {
                  // Игнорируем ошибки автоплея
                });
              }
              setShouldPlay(true);
            } else if (!isInView && shouldPlay) {
              node.pause();
              setShouldPlay(false);
            }
          },
          {
            rootMargin: options.rootMargin || '0px',
            threshold: options.threshold || 0.3,
            root: options.root || null,
          },
        );

        observerRef.current.observe(node);
      }
    },
    [shouldPlay, options.rootMargin, options.threshold, options.root],
  );

  useEffect(
    () => () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    },
    [],
  );

  const forcePlay = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      video.play().catch(() => false);
      setShouldPlay(true);
    }
  }, []);

  return [setVideoRef, shouldPlay, forcePlay];
}
